/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativosPublico, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaTelcos.module.scss";

// =====================================================
// DATOS IPSOS TELEFONÍA MÓVIL (Base: 400 casos)
// =====================================================
const IPSOS_MOVIL = {
  operadores: {
    ENTEL: { share: 0.53, precioMes: 20000 },
    WOM: { share: 0.334, precioMes: 18000 },
    CLARO: { share: 0.11, precioMes: 15000 },
    VTR: { share: 0.026, precioMes: 17000 },
  },
  tipoPlan: {
    postpago: 1, // 100%
    prepago: 0,
  },
  telefonoCorporativo: 0.362, // 36.2% tiene teléfono de empresa (B2B)
  cambiariaPlan: 0.299, // 29.9% cambiaría por oferta tentadora
  noCambiaria: 0.701, // 70.1% no cambiaría
};

// =====================================================
// DATOS IPSOS INTERNET HOGAR (Base: 400 casos)
// =====================================================
const IPSOS_HOGAR = {
  tieneFibra: 0.9, // 90% tiene internet fibra en hogar
  proveedores: {
    Movistar: 0.354,
    VTR: 0.316,
    Entel: 0.15,
    Mundo: 0.104,
    Claro: 0.057,
    GTD: 0.019,
  },
  precioMensualPromedio: 18000, // CLP
};

// Escenarios de captación
const ESCENARIOS_CAPTACION = [5, 10, 15, 20];

export const Telcos = () => {
  const { formatNumber, formatLocal, convertCLP, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [operadorSeleccionado, setOperadorSeleccionado] = useState("ENTEL");
  const [proveedorHogarSeleccionado, setProveedorHogarSeleccionado] = useState("Movistar");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [vistaActiva, setVistaActiva] = useState("movil"); // 'movil' o 'hogar'
  const [verAnual, setVerAnual] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativosPublico({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // =====================================================
  // CÁLCULOS TELEFONÍA MÓVIL
  // =====================================================
  const resultadosMovil = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Clientes B2C (personal) y B2B (corporativo)
    const clientesB2B = Math.round(totalEjecutivos * IPSOS_MOVIL.telefonoCorporativo);
    const clientesB2C = totalEjecutivos; // Todos tienen teléfono personal
    const clientesTotales = clientesB2C + clientesB2B;

    // Desglose por operador
    const operadores = {};
    let mercadoMensualTotal = 0;

    Object.entries(IPSOS_MOVIL.operadores).forEach(([nombre, data]) => {
      const clientesB2COp = Math.round(clientesB2C * data.share);
      const clientesB2BOp = Math.round(clientesB2B * data.share);
      const totalOp = clientesB2COp + clientesB2BOp;
      const mercadoMensual = totalOp * data.precioMes;

      operadores[nombre] = {
        share: data.share * 100,
        clientesB2C: clientesB2COp,
        clientesB2B: clientesB2BOp,
        clientesTotales: totalOp,
        precioMes: data.precioMes,
        mercadoMensual,
        mercadoAnual: mercadoMensual * 12,
      };

      mercadoMensualTotal += mercadoMensual;
    });

    // Oportunidad de churn
    const clientesCambiarian = Math.round(clientesB2C * IPSOS_MOVIL.cambiariaPlan);

    return {
      totalEjecutivos,
      clientesB2C,
      clientesB2B,
      clientesTotales,
      operadores,
      mercadoMensualTotal,
      mercadoAnualTotal: mercadoMensualTotal * 12,
      clientesCambiarian,
      pctCambiarian: IPSOS_MOVIL.cambiariaPlan * 100,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULOS INTERNET HOGAR
  // =====================================================
  const resultadosHogar = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Hogares con fibra
    const hogaresFibra = Math.round(totalEjecutivos * IPSOS_HOGAR.tieneFibra);

    // Desglose por proveedor
    const proveedores = {};
    let mercadoMensualTotal = 0;

    Object.entries(IPSOS_HOGAR.proveedores).forEach(([nombre, share]) => {
      const hogares = Math.round(hogaresFibra * share);
      const mercadoMensual = hogares * IPSOS_HOGAR.precioMensualPromedio;

      proveedores[nombre] = {
        share: share * 100,
        hogares,
        precioMes: IPSOS_HOGAR.precioMensualPromedio,
        mercadoMensual,
        mercadoAnual: mercadoMensual * 12,
      };

      mercadoMensualTotal += mercadoMensual;
    });

    return {
      totalEjecutivos,
      hogaresFibra,
      pctFibra: IPSOS_HOGAR.tieneFibra * 100,
      proveedores,
      mercadoMensualTotal,
      mercadoAnualTotal: mercadoMensualTotal * 12,
    };
  }, [totalEjecutivos]);

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN MÓVIL
  // =====================================================
  const calcularOportunidadMovil = useCallback(
    (operador, pctCaptacion) => {
      if (!resultadosMovil) return null;

      const datosOp = resultadosMovil.operadores[operador];
      if (!datosOp) return null;

      // Del 29.9% que cambiaría, captar un % específico
      const clientesPotenciales = resultadosMovil.clientesCambiarian;
      const clientesCaptados = Math.round(clientesPotenciales * (pctCaptacion / 100));
      const mercadoGanadoMensual = clientesCaptados * datosOp.precioMes;

      return {
        clientesPotenciales,
        clientesCaptados,
        mercadoGanadoMensual,
        mercadoGanadoAnual: mercadoGanadoMensual * 12,
        costoAdquisicionRef: 18000000, // Pauta 3 meses referencia
      };
    },
    [resultadosMovil]
  );

  // =====================================================
  // CÁLCULO OPORTUNIDAD CAPTACIÓN HOGAR
  // =====================================================
  const calcularOportunidadHogar = useCallback(
    (proveedor, pctCaptacion) => {
      if (!resultadosHogar) return null;

      const datosP = resultadosHogar.proveedores[proveedor];
      if (!datosP) return null;

      // Captar del mercado total de hogares fibra
      const clientesCaptados = Math.round(resultadosHogar.hogaresFibra * (pctCaptacion / 100));
      const mercadoGanadoMensual = clientesCaptados * IPSOS_HOGAR.precioMensualPromedio;

      return {
        hogaresTotales: resultadosHogar.hogaresFibra,
        clientesCaptados,
        mercadoGanadoMensual,
        mercadoGanadoAnual: mercadoGanadoMensual * 12,
      };
    },
    [resultadosHogar]
  );

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.telcosPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>TELECOMUNICACIONES</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.telcosContent}>
        {/* Tabs para cambiar entre Móvil y Hogar */}
        <div className={styles.tabsContainer}>
          <button
            className={cx(styles.tab, vistaActiva === "movil" && styles.tabActivo)}
            onClick={() => setVistaActiva("movil")}
          >
            Telefonía Móvil
          </button>
          <button
            className={cx(styles.tab, vistaActiva === "hogar" && styles.tabActivo)}
            onClick={() => setVistaActiva("hogar")}
          >
            Internet Hogar
          </button>
        </div>

        {/* =====================================================
            VISTA TELEFONÍA MÓVIL
            ===================================================== */}
        {vistaActiva === "movil" && resultadosMovil && (
          <>
            {/* Tabla resumen */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thSmall)}>B2C</th>
                    <th className={cx("text-center", styles.thSmall)}>B2B</th>
                    <th className={cx("text-center", styles.thSmall)}>Churn</th>
                    <th className={cx("text-center", styles.thMercado)}>
                      <div className={styles.headerConsumos}>
                        <span>Mercado</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <span className={styles.badgeIpsos}>100%</span>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesB2C)}</div>
                        <div className={styles.subtexto}>líneas personales</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <span className={styles.badgeIpsos}>36.2%</span>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesB2B)}</div>
                        <div className={styles.subtexto}>líneas corporativas</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaOportunidad)}>
                      <div className={styles.celdaContenido}>
                        <span className={cx(styles.badgeIpsos, styles.badgeOportunidad)}>29.9%</span>
                        <div className={styles.valorGrande}>{formatNumber(resultadosMovil.clientesCambiarian)}</div>
                        <div className={styles.subtexto}>cambiarían de operador</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaTotal)}>
                      <div className={styles.valorDestacado}>
                        {formatLocal(
                          convertCLP(verAnual ? resultadosMovil.mercadoAnualTotal : resultadosMovil.mercadoMensualTotal)
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Market Share por Operador */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Operador</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosMovil.operadores).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <span className={styles.badgeIpsos}>{data.share.toFixed(0)}%</span>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Clientes</span>
                        <span className={styles.metricaValor}>{formatNumber(data.clientesTotales)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. 100% tiene plan postpago. 36.2% tiene
                teléfono corporativo (B2B). 29.9% cambiaría de operador por oferta tentadora.
              </p>
            </div>
          </>
        )}

        {/* =====================================================
            VISTA INTERNET HOGAR
            ===================================================== */}
        {vistaActiva === "hogar" && resultadosHogar && (
          <>
            {/* Tabla resumen */}
            <div className={styles.tablaContainer}>
              <table className={cx("table table-sm", styles.tablaResultados)}>
                <thead className="table-dark">
                  <tr>
                    <th className={cx("text-center", styles.thSmall)}>Tienen fibra</th>
                    <th className={cx("text-center", styles.thSmall)}>Precio prom.</th>
                    <th className={cx("text-center", styles.thMercado)}>
                      <div className={styles.headerConsumos}>
                        <span>Mercado</span>
                        <div className={styles.toggleContainer}>
                          <span className={cx(styles.toggleLabel, !verAnual && styles.toggleActive)}>mes</span>
                          <label className={styles.toggleSwitch}>
                            <input type="checkbox" checked={verAnual} onChange={(e) => setVerAnual(e.target.checked)} />
                            <span className={styles.toggleSlider} />
                          </label>
                          <span className={cx(styles.toggleLabel, verAnual && styles.toggleActive)}>año</span>
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={cx("text-center align-middle", styles.celdaSi)}>
                      <div className={styles.celdaContenido}>
                        <span className={styles.badgeIpsos}>90%</span>
                        <div className={styles.valorGrande}>{formatNumber(resultadosHogar.hogaresFibra)}</div>
                        <div className={styles.subtexto}>hogares conectados</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle")}>
                      <div className={styles.celdaContenido}>
                        <div className={styles.valorGrande}>
                          {formatLocal(convertCLP(IPSOS_HOGAR.precioMensualPromedio))}
                        </div>
                        <div className={styles.subtexto}>mensual por hogar</div>
                      </div>
                    </td>
                    <td className={cx("text-center align-middle", styles.celdaTotal)}>
                      <div className={styles.valorDestacado}>
                        {formatLocal(
                          convertCLP(verAnual ? resultadosHogar.mercadoAnualTotal : resultadosHogar.mercadoMensualTotal)
                        )}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Market Share por Proveedor */}
            <div className={styles.marketShareContainer}>
              <h4 className={styles.marketShareTitulo}>Market Share por Proveedor</h4>
              <div className={styles.marketShareGrid}>
                {Object.entries(resultadosHogar.proveedores).map(([nombre, data]) => (
                  <div key={nombre} className={styles.marketShareCard}>
                    <div className={styles.cardNombre}>{nombre}</div>
                    <div className={styles.cardShare}>
                      <span className={styles.badgeIpsos}>{data.share.toFixed(0)}%</span>
                    </div>
                    <div className={styles.cardMetricas}>
                      <div>
                        <span className={styles.metricaLabel}>Hogares</span>
                        <span className={styles.metricaValor}>{formatNumber(data.hogares)}</span>
                      </div>
                      <div>
                        <span className={styles.metricaLabel}>Mercado/{verAnual ? "año" : "mes"}</span>
                        <span className={styles.metricaValor}>
                          {formatLocal(convertCLP(verAnual ? data.mercadoAnual : data.mercadoMensual))}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
                Simular Oportunidad
              </button>
            </div>

            {/* Disclaimer */}
            <div className={styles.disclaimer}>
              <p>
                <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos. 90% tiene internet fibra en el hogar.
                Precio promedio mensual: $18.000 CLP.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> ejecutivos
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork }) => (
                    <tr
                      key={id}
                      onClick={() => toggleCheck(id)}
                      className={cx(checkedBuildings[id] && "table-success")}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="text-center">
                        {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                      </td>
                      <td className="text-center">{edificio.toUpperCase()}</td>
                      <td className="text-center">{ubicacion}</td>
                      <td className="text-center">
                        {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                      </td>
                      <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (resultadosMovil || resultadosHogar) && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad - {vistaActiva === "movil" ? "Telefonía Móvil" : "Internet Hogar"}</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              {vistaActiva === "movil" && resultadosMovil && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Operador objetivo:</label>
                      <select
                        value={operadorSeleccionado}
                        onChange={(e) => setOperadorSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_MOVIL.operadores).map((op) => (
                          <option key={op} value={op}>
                            {op}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación del churn:</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION.map((p) => (
                          <option key={p} value={p}>
                            {p}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadMovil(operadorSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Clientes que cambiarían (29.9%):</span>
                          <span className={styles.resultadoValor}>{formatNumber(oportunidad.clientesPotenciales)}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            +{formatNumber(oportunidad.clientesCaptados)} clientes
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/mes:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoMensual))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/año (contrato 12m):</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {vistaActiva === "hogar" && resultadosHogar && (
                <div className={styles.simuladorContainer}>
                  <div className={styles.simuladorControles}>
                    <div className={styles.controlGroup}>
                      <label>Proveedor objetivo:</label>
                      <select
                        value={proveedorHogarSeleccionado}
                        onChange={(e) => setProveedorHogarSeleccionado(e.target.value)}
                        className="form-select form-select-sm"
                      >
                        {Object.keys(IPSOS_HOGAR.proveedores).map((p) => (
                          <option key={p} value={p}>
                            {p}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.controlGroup}>
                      <label>% captación mercado:</label>
                      <select
                        value={porcentajeCaptacion}
                        onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                        className="form-select form-select-sm"
                      >
                        {ESCENARIOS_CAPTACION.map((p) => (
                          <option key={p} value={p}>
                            {p}%
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(() => {
                    const oportunidad = calcularOportunidadHogar(proveedorHogarSeleccionado, porcentajeCaptacion);
                    if (!oportunidad) return null;

                    return (
                      <div className={styles.simuladorResultados}>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Hogares con fibra (90%):</span>
                          <span className={styles.resultadoValor}>{formatNumber(oportunidad.hogaresTotales)}</span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Captación al {porcentajeCaptacion}%:</span>
                          <span className={styles.resultadoValorDestacado}>
                            +{formatNumber(oportunidad.clientesCaptados)} hogares
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/mes:</span>
                          <span className={styles.resultadoValor}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoMensual))}
                          </span>
                        </div>
                        <div className={styles.resultadoItem}>
                          <span className={styles.resultadoLabel}>Mercado ganado/año:</span>
                          <span className={styles.resultadoValorDestacado}>
                            {formatLocal(convertCLP(oportunidad.mercadoGanadoAnual))}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Telcos;
